import { Fragment } from 'react';
import { ArticleBody } from '../ArticleBody';

/**
 * Structured, human-readable rendering of a work-item payload — replaces the
 * raw JSON dump in the review panel. Generic on purpose: agent payloads range
 * from flat posts (body/category/hashtags) to nested audits and arrays of
 * recommendation objects, and new agents must render acceptably with zero
 * changes here. Media keys are excluded — MediaBlock owns those.
 */

const MEDIA_KEYS = new Set(['media', 'media_skipped', 'needs_photo', 'photo_note']);
const HEADING_KEYS = ['title', 'subject'];
const BODY_KEYS = ['body_markdown', 'body', 'memo', 'reply'];

const label = (k: string) => k.replaceAll('_', ' ');
const isIsoDate = (v: string) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function Value({ v }: { v: unknown }) {
  if (v === null || v === undefined) return <span className="muted">—</span>;
  if (typeof v === 'boolean') return <span>{v ? 'yes' : 'no'}</span>;
  if (typeof v === 'number') return <span className="mono">{v.toLocaleString()}</span>;
  if (typeof v === 'string') {
    if (isIsoDate(v)) return <span>{new Date(v).toLocaleString()}</span>;
    if (v.includes('\n') || v.length > 200) return <ArticleBody markdown={v} />;
    return <span style={{ overflowWrap: 'anywhere' }}>{v}</span>;
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="muted">none</span>;
    if (v.every((x) => !isPlainObject(x) && !Array.isArray(x)))
      return <span style={{ overflowWrap: 'anywhere' }}>{v.map((x) => String(x)).join(' · ')}</span>;
    if (v.every(isPlainObject)) {
      const flat = v.every((x) => Object.values(x).every((y) => typeof y !== 'object' || y === null));
      if (flat && v.length > 1) {
        const cols = [...new Set(v.flatMap((x) => Object.keys(x)))];
        return (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c}>{label(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {v.map((row, i) => (
                  <tr key={i}>
                    {cols.map((c) => (
                      <td key={c}>
                        <Value v={row[c]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      return (
        <div className="stack">
          {v.map((x, i) => (
            <div key={i} className="kv-card">
              <KeyValues obj={x} />
            </div>
          ))}
        </div>
      );
    }
  }
  if (isPlainObject(v)) return <KeyValues obj={v} />;
  return <span className="mono">{JSON.stringify(v)}</span>;
}

function KeyValues({ obj, skip }: { obj: Record<string, unknown>; skip?: Set<string> }) {
  const entries = Object.entries(obj).filter(([k]) => !skip?.has(k));
  if (entries.length === 0) return null;
  return (
    <dl className="kv">
      {entries.map(([k, v]) => (
        <Fragment key={k}>
          <dt>{label(k)}</dt>
          <dd>
            <Value v={v} />
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function PayloadView({ payload }: { payload: Record<string, unknown> }) {
  const headingKey = HEADING_KEYS.find((k) => typeof payload[k] === 'string');
  const bodyKey = BODY_KEYS.find((k) => typeof payload[k] === 'string');
  const skip = new Set([...MEDIA_KEYS, ...(headingKey ? [headingKey] : []), ...(bodyKey ? [bodyKey] : [])]);

  return (
    <div>
      {headingKey && <h4 style={{ margin: '10px 0 2px' }}>{payload[headingKey] as string}</h4>}
      {bodyKey && (
        <div className="article-scroll" style={{ maxHeight: 380 }}>
          <ArticleBody markdown={payload[bodyKey] as string} />
        </div>
      )}
      <KeyValues obj={payload} skip={skip} />
    </div>
  );
}
